import EditablePayrollRow from "./EditablePayrollRow";
import "./PayrollRulesPage.css";
// src/popup/AddOvertimePopup.js
import React, { useState, useEffect, useRef } from "react";
import {
  FaSave,
  FaSearch,
  FaArrowLeft,
  FaBusinessTime,
} from "react-icons/fa";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";

// Popup-komponent för att hantera övertidsregler
export default function AddOvertimePopup({ isOpen, onClose, onSave, auditSource, companyRootUid }) {
  const { activeCompany } = useCompany();

  const formRef = useRef(null);

  // State för filtrering och UI
  const [searchTerm, setSearchTerm] = useState("");

  // State för formulär
  const [ruleName, setRuleName] = useState("");
  const [overtimePercent, setOvertimePercent] = useState("");
  const [description, setDescription] = useState("");

  // State för data och laddning
  const [editingId, setEditingId] = useState(null);
  const [overtimeRules, setOvertimeRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Hämta företagskontots UID (använder companyAccountUid om det finns)
  const accountToUse = companyRootUid || activeCompany?.companyAccountUid;

  // Hjälpfunktion för att hämta collection för övertidsregler
  const getOvertimeRulesCollection = () => {
    if (!accountToUse) return null;
    return collection(db, "users", accountToUse, "overtimeRules");
  };

  // Hjälpfunktion för att hämta dokument för en specifik övertidsregel
  const getOvertimeRuleDoc = (ruleId) => {
    if (!accountToUse || !ruleId) return null;
    return doc(db, "users", accountToUse, "overtimeRules", ruleId);
  };

  // Hämta övertidsregler från Firestore i realtid
  useEffect(() => {
    if (!accountToUse || !isOpen) return;

    const overtimeRulesRef = getOvertimeRulesCollection();
    if (!overtimeRulesRef) return;

    const q = query(overtimeRulesRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rules = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));
        setOvertimeRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av övertidsregler:", error);
      }
    );

    return () => unsubscribe();
  }, [accountToUse, isOpen]);

  // Stäng popup om den inte är öppen
  if (!isOpen) return null;

  // Återställ formulär
  const resetForm = () => {
    setRuleName("");
    setOvertimePercent("");
    setDescription("");
    setEditingId(null);
    setSaveSuccess(false);
  };

  // Hantera formulärinskickning (skapa eller uppdatera)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountToUse) return;

    setLoading(true);
    setSaveSuccess(false);

    try {
      const trimmedName = ruleName.trim();
      const trimmedDescription = description.trim();
      const percentageValue = Number(overtimePercent);

      // Validering
      if (!trimmedName || overtimePercent === "") {
        setLoading(false);
        return;
      }

      // Uppdatera befintlig regel
      if (editingId) {
        const overtimeRuleRef = getOvertimeRuleDoc(editingId);
        if (!overtimeRuleRef) return;

        const updatedRule = {
          ...(auditSource ? { auditMutation: { source: auditSource, id: doc(collection(db, "popupShiftOperations")).id } } : {}),
          name: trimmedName,
          percentage: percentageValue,
          description: trimmedDescription,
          updatedAt: new Date().toISOString(),
        };

        await updateDoc(overtimeRuleRef, updatedRule);

        if (onSave) {
          onSave({
            id: editingId,
            ...updatedRule,
            action: "update",
          });
        }

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } 
      // Skapa ny regel
      else {
        const overtimeRulesRef = getOvertimeRulesCollection();
        if (!overtimeRulesRef) return;

        const newRule = {
          ...(auditSource ? { auditMutation: { source: auditSource, id: doc(collection(db, "popupShiftOperations")).id } } : {}),
          name: trimmedName,
          percentage: percentageValue,
          description: trimmedDescription,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: accountToUse,
        };

        const docRef = await addDoc(overtimeRulesRef, newRule);

        if (onSave) {
          onSave({
            id: docRef.id,
            ...newRule,
            action: "create",
          });
        }

        resetForm();

        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Fel vid sparande av övertidsregel:", error);
    } finally {
      setLoading(false);
    }
  };

  // Hantera redigering av en övertidsregel
  // Hantera borttagning av en övertidsregel
  const handleDelete = async (id) => {
    if (!accountToUse || !id) return;

    if (window.confirm("Är du säker på att du vill ta bort denna övertidsregel?")) {
      try {
        const deletedRule = overtimeRules.find((rule) => rule.id === id);
        const overtimeRuleRef = getOvertimeRuleDoc(id);
        if (!overtimeRuleRef) return;

        if (auditSource === "PopupShift") {
          const batch = writeBatch(db);
          batch.set(doc(db, "users", accountToUse, "popupShiftDeletions", `overtimeRules_${id}`), { occurredAt: serverTimestamp() });
          batch.delete(overtimeRuleRef);
          await batch.commit();
        } else {
          await deleteDoc(overtimeRuleRef);
        }

        if (onSave) {
          onSave({ ...deletedRule, action: "delete" });
        }
      } catch (error) {
        console.error("Fel vid borttagning av övertidsregel:", error);
      }
    }
  };

  // Stäng popup och återställ alla states
  const handleClose = () => {
    resetForm();
    setSearchTerm("");


    onClose();
  };



  // Filtrera övertidsregler baserat på sökterm
  const filteredOvertimeRules = overtimeRules.filter((rule) => {
    const search = searchTerm.toLowerCase();

    return (
      rule.name?.toLowerCase().includes(search) ||
      rule.description?.toLowerCase().includes(search) ||
      String(rule.percentage ?? "").includes(search)
    );
  });

  // Hjälpfunktion för att visa multiplikatorn för övertid
  const getMultiplierText = (percentage) => {
    const value = Number(percentage || 0);
    const multiplier = (1 + value / 100).toFixed(value % 100 === 0 ? 0 : 1);
    return `${multiplier}×`;
  };

  // Stilar för komponenten
  const styles = {
    header: {
      padding: "24px 24px 16px 24px",
      borderBottom: "1px solid #eef2f7",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#ffffff",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    title: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    closeButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#6b7280",
      transition: "all 0.2s",
    },
    backButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#007bff",
      transition: "all 0.2s",
      marginRight: "8px",
    },
    content: {
      padding: "24px",
    },
    introText: {
      fontSize: "14px",
      lineHeight: 1.6,
      color: "#6b7280",
      marginBottom: "20px",
    },
    searchSection: {
      marginBottom: "20px",
    },
    searchContainer: {
      position: "relative",
      marginBottom: "12px",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
    },
    searchInput: {
      width: "100%",
      padding: "12px 16px 12px 40px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    createButtonRow: {
      display: "flex",
      justifyContent: "flex-start",
    },
    createButton: {
      padding: "10px 20px",
      borderRadius: "12px",
      border: "none",
      backgroundColor: "#007bff",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      whiteSpace: "nowrap",
      transition: "background-color 0.2s",
    },
    rulesList: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginTop: "20px",
    },
    ruleItem: {
      padding: "18px 20px",
      borderRadius: "16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      border: "1px solid #eef2f7",
    },
    ruleInfo: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: 0,
    },
    ruleName: {
      fontSize: "18px",
      fontWeight: 700,
      color: "#111827",
    },
    ruleMeta: {
      fontSize: "14px",
      color: "#4b5563",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
    },
    percentBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
      borderRadius: "999px",
      padding: "4px 10px",
      fontSize: "12px",
      fontWeight: 700,
      width: "fit-content",
    },
    descriptionText: {
      fontSize: "13px",
      color: "#6b7280",
      lineHeight: 1.4,
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      marginLeft: "12px",
      flexShrink: 0,
    },
    editButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#4b5563",
      transition: "all 0.2s",
    },
    deleteButton: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "8px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#dc2626",
      transition: "all 0.2s",
    },
    panelHeader: {
      padding: "24px 24px 16px 24px",
      borderBottom: "1px solid #eef2f7",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      backgroundColor: "#ffffff",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    panelTitle: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    panelForm: {
      padding: "24px",
    },
    formGroup: {
      marginBottom: "24px",
    },
    label: {
      display: "block",
      fontSize: "14px",
      fontWeight: 600,
      color: "#374151",
      marginBottom: "8px",
    },
    helperText: {
      fontSize: "12px",
      color: "#6b7280",
      marginBottom: "10px",
      lineHeight: 1.5,
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      fontSize: "14px",
      outline: "none",
      minHeight: "90px",
      resize: "vertical",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    inputContainer: {
      position: "relative",
    },
    percentSymbol: {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280",
      fontSize: "14px",
      fontWeight: 500,
    },
    previewBox: {
      marginTop: "16px",
      backgroundColor: "#f8fafc",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "14px",
    },
    previewTitle: {
      fontSize: "13px",
      fontWeight: 700,
      color: "#374151",
      marginBottom: "8px",
    },
    previewText: {
      fontSize: "13px",
      color: "#4b5563",
      lineHeight: 1.5,
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      marginTop: "32px",
    },
    saveButton: {
      flex: 1,
      padding: "14px 20px",
      borderRadius: "12px",
      border: "none",
      backgroundColor: "#007bff",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "background-color 0.2s",
      opacity: loading ? 0.7 : 1,
    },
    cancelButton: {
      flex: 1,
      padding: "14px 20px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      backgroundColor: "#ffffff",
      color: "#4b5563",
      fontSize: "15px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "background-color 0.2s",
    },
    divider: {
      height: "1px",
      backgroundColor: "#eef2f7",
      margin: "24px 0",
    },
    infoText: {
      fontSize: "13px",
      color: "#6b7280",
      marginTop: "16px",
      fontStyle: "italic",
      textAlign: "center",
    },
    emptyState: {
      textAlign: "center",
      padding: "48px 20px",
      color: "#6b7280",
      fontSize: "14px",
      border: "1px dashed #d1d5db",
      borderRadius: "16px",
      marginTop: "20px",
    },
    successMessage: {
      backgroundColor: "#10b981",
      color: "#ffffff",
      padding: "10px",
      borderRadius: "8px",
      textAlign: "center",
      marginTop: "16px",
      fontSize: "14px",
    },
  };

  Object.assign(styles, {
    header: { padding: "20px 12px 16px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 20 },
    title: { margin: 0, display: "flex", alignItems: "center", gap: 12, fontSize: 24, fontWeight: 800, color: "#111827" },
    content: { padding: 0 },
    buttonGroup: { display: "flex", justifyContent: "flex-end", marginTop: 20 },
    saveButton: { ...styles.saveButton, flex: "initial", borderRadius: 8, padding: "10px 20px", backgroundColor: "#1677ff" },
  });
  return (
    <section className="payroll-rules-page" style={{ width: "100%", minWidth: 0, background: "#f7f7f8" }} aria-label="Övertidsregler">
      <header style={styles.header}>
        <button type="button" onClick={handleClose} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", border: "1px solid #3b82f6", borderRadius: 8, color: "#2563eb", background: "white", cursor: "pointer", fontWeight: 700 }}><FaArrowLeft />Tillbaka till löner</button>
        <h1 style={styles.title}><FaBusinessTime color="#1677ff" />Övertidsregler</h1>
      </header>
      <div style={{ padding: "0 12px 16px" }}>
        <section style={{ padding: 20, border: "1px solid #d1d5db", borderRadius: 8, background: "white", marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 20px", fontSize: 16 }}>{editingId ? "Redigera" : "Skapa ny övertidsregel"}</h2>
              <form ref={formRef} className="payroll-rule-form" onSubmit={handleSubmit}>
                {/* Namn-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Namn</label>
                  <input
                    type="text"
                    className="payroll-field" style={styles.input}
                    value={ruleName}
                    onChange={(e) => setRuleName(e.target.value)}
                    placeholder="t.ex. Enkel övertid"
                    required
                  />
                </div>

                {/* Procent-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>
                    Övertidsersättning (i % utöver timlönen)
                  </label>
                  <div style={styles.helperText}>
                    T.ex. 50 betyder 50% extra (ger 1,5×). 100 betyder 100% extra (ger 2×).
                  </div>

                  <div style={styles.inputContainer}>
                    <input
                      type="number"
                      className="payroll-field" style={styles.input}
                      value={overtimePercent}
                      onChange={(e) => setOvertimePercent(e.target.value)}
                      placeholder="t.ex. 50"
                      required
                      min="0"
                      step="1"
                    />
                    <span style={styles.percentSymbol}>%</span>
                  </div>

                  {/* Förhandsvisning */}
                  {overtimePercent !== "" && (
                    <div style={styles.previewBox}>
                      <div style={styles.previewTitle}>Förhandsvisning</div>
                      <div style={styles.previewText}>
                        {Number(overtimePercent)}% extra innebär att ersättningen blir{" "}
                        <strong>{getMultiplierText(overtimePercent)}</strong> av timlönen.
                      </div>
                    </div>
                  )}
                </div>

                {/* Beskrivning-fält */}
                <div style={styles.formGroup}>
                  <label className="payroll-label" style={styles.label}>Beskrivning (valfri)</label>
                  <textarea
                    className="payroll-field payroll-textarea" style={styles.textarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="t.ex. Gäller efter två timmar"
                  />
                </div>



                {/* Knappar */}
                <div style={styles.buttonGroup}>

                  <button
                    type="submit"
                    className="payroll-primary-button" style={styles.saveButton}
                    disabled={loading}
                  >
                    <FaSave />
                    {loading ? "Sparar..." : editingId ? "Uppdatera" : "Skapa"}
                  </button>
                </div>

                {/* Succémeddelande */}
                {saveSuccess && (
                  <div className="payroll-success" style={styles.successMessage}>
                    Övertidsregeln sparades framgångsrikt!
                  </div>
                )}


              </form>
        </section>
        <section><h2 style={{ fontSize: 16, margin: "0 0 20px" }}>Alla övertidsregler</h2>
            <div style={styles.content}>

              {/* Sökfält och skapa-knapp */}
              <div style={styles.searchSection}>
                <div style={styles.searchContainer}>
                  <FaSearch style={styles.searchIcon} size={16} />
                  <input
                    type="text"
                    className="payroll-field payroll-search" style={styles.searchInput}
                    placeholder="Sök övertidsregel..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>


              </div>

              <div className="payroll-table-wrap"><table className="payroll-table">
                <thead><tr><th>Namn</th><th>Extra ersättning</th><th>Beskrivning</th><th>Åtgärder</th></tr></thead>
                <tbody>{filteredOvertimeRules.map(rule => <EditablePayrollRow auditSource={auditSource} key={rule.id} item={rule} companyId={accountToUse} collectionName="overtimeRules" onDelete={item => handleDelete(item.id)} onSaved={onSave} columns={[
                  { value: rule.name, fields: [{ key: "name", label: "Namn i raden", required: true }] },
                  { value: `${Number(rule.percentage || 0).toLocaleString("sv-SE")} % (${getMultiplierText(rule.percentage)})`, fields: [{ key: "percentage", label: "Procent i raden", type: "number", min: 0,  required: true }] },
                  { value: rule.description || "–", fields: [{ key: "description", label: "Beskrivning i raden", type: "textarea" }] },
                ]} />)}
                {!filteredOvertimeRules.length && <tr><td colSpan={4} className="payroll-empty">Inga regler att visa.</td></tr>}</tbody>
              </table></div>
            </div>
        </section>
      </div>
    </section>
  );
}
